import { z } from 'zod';
import { keepFillQty } from './inventory';

export const vanConfigGetInput = z.object({ vanId: z.string().uuid() }).strict();
export type VanConfigGetInput = z.infer<typeof vanConfigGetInput>;
export const vanPointPair = z
  .object({
    itemId: z.string().uuid(),
    keepAtLeast: keepFillQty,
    fillTo: keepFillQty,
  })
  .strict()
  .refine((p) => p.fillTo >= p.keepAtLeast, {
    path: ['fillTo'],
    message: 'Fill must be at least Keep.',
  });

/** A full watched list; bounded before any work and bound to an explicit revision. */
export const vanConfigSetInput = z
  .object({
    clientId: z.string().uuid(),
    mutationId: z.string().uuid(),
    vanId: z.string().uuid(),
    expectedRevision: z.string().regex(/^[a-f0-9]{64}$/),
    feederId: z.string().uuid().nullable(),
    points: z.array(vanPointPair).max(5000),
  })
  .strict()
  .superRefine((input, ctx) => {
    if (new Set(input.points.map((p) => p.itemId)).size !== input.points.length)
      ctx.addIssue({
        code: 'custom',
        path: ['points'],
        message: 'Watch each part once.',
      });
  });
export type VanConfigSetInput = z.infer<typeof vanConfigSetInput>;

const id = z.string().uuid();
const revision = z.string().regex(/^[a-f0-9]{64}$/);
const name = z.string().trim().min(1).max(120);
const keys = { clientId: id, mutationId: id };
const packQty = keepFillQty.refine((n) => n > 0, 'Purchase pack must be positive.');
const editProposal = z
  .object({
    kind: z.literal('edit'),
    templateId: id,
    name,
    points: z.array(vanPointPair).max(5000),
  })
  .strict();
const pairChange = { itemId: id, keep: keepFillQty, fill: keepFillQty };
const vanChange = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('local'), ...pairChange }).strict(),
  z.object({ kind: z.literal('vanOnly'), ...pairChange }).strict(),
  z.object({ kind: z.literal('reset'), itemId: id }).strict(),
  z.object({ kind: z.literal('stopWatching'), itemId: id }).strict(),
  z
    .object({ kind: z.literal('pack'), itemId: id, reorderQty: packQty.nullable() })
    .strict(),
]);
export const vanTemplateProposal = z
  .discriminatedUnion('kind', [
    editProposal,
    z
      .object({
        kind: z.literal('apply'),
        vanId: id,
        templateId: id,
        preserveLocalItemIds: z.array(id).max(5000),
      })
      .strict(),
    z.object({ kind: z.literal('detach'), vanId: id }).strict(),
    z.object({ kind: z.literal('delete'), templateId: id }).strict(),
    z
      .object({
        kind: z.literal('configureVan'),
        vanId: id,
        feederId: id.nullable(),
        changes: z.array(vanChange).max(5000),
      })
      .strict(),
  ])
  .superRefine((p, ctx) => {
    const unique = (ids: string[], path: string) => {
      if (new Set(ids).size !== ids.length)
        ctx.addIssue({ code: 'custom', path: [path], message: 'Choose each part once.' });
    };
    if (p.kind === 'edit')
      unique(
        p.points.map((p) => p.itemId),
        'points',
      );
    if (p.kind === 'apply') unique(p.preserveLocalItemIds, 'preserveLocalItemIds');
    if (p.kind === 'configureVan') {
      unique(
        p.changes.filter((c) => c.kind !== 'pack').map((c) => c.itemId),
        'changes',
      );
      unique(
        p.changes.filter((c) => c.kind === 'pack').map((c) => c.itemId),
        'changes',
      );
      p.changes.forEach((c, i) => {
        if ((c.kind === 'local' || c.kind === 'vanOnly') && c.fill < c.keep)
          ctx.addIssue({
            code: 'custom',
            path: ['changes', i, 'fill'],
            message: 'Fill must be at least Keep.',
          });
      });
    }
  });
export type VanTemplateProposal = z.infer<typeof vanTemplateProposal>;
export const vanTemplateCreateInput = z
  .object({
    ...keys,
    name,
    source: z.discriminatedUnion('kind', [
      z.object({ kind: z.literal('blank') }).strict(),
      z.object({ kind: z.literal('template'), id, expectedRevision: revision }).strict(),
      z.object({ kind: z.literal('van'), id, expectedRevision: revision }).strict(),
    ]),
  })
  .strict();
export type VanTemplateCreateInput = z.infer<typeof vanTemplateCreateInput>;
export const vanTemplatePreviewInput = z
  .object({
    proposal: vanTemplateProposal,
    page: z
      .object({
        vanOffset: z.number().int().min(0).max(50000),
        rowOffset: z.number().int().min(0).max(50000),
      })
      .strict(),
    expectedPolicyRevision: revision.optional(),
  })
  .strict();
export type VanTemplatePreviewInput = z.infer<typeof vanTemplatePreviewInput>;
export const vanTemplateCommitInput = z
  .object({ ...keys, proposal: vanTemplateProposal, expectedRevision: revision })
  .strict();
export type VanTemplateCommitInput = z.infer<typeof vanTemplateCommitInput>;
export const vanTemplateGetInput = z
  .object({ templateId: id, offset: z.number().int().min(0).max(50000).default(0) })
  .strict();
export const vanTemplateListInput = z
  .object({ offset: z.number().int().min(0).max(50000).default(0) })
  .strict()
  .default({ offset: 0 });
export const vanPolicyContextInput = z.object({ itemId: id }).strict();

/** New stock intents carry exact integer hundredths; they never accept prices or source IDs. */
const restockHundredths = z.number().int().safe().nonnegative();
const sendItem = z.object({ itemId: id, sendHundredths: restockHundredths }).strict();
const checkItem = z
  .object({
    itemId: id,
    missingHundredths: restockHundredths.default(0),
    damagedHundredths: restockHundredths.default(0),
  })
  .strict();
function uniqueRestockItems(
  input: { items?: { itemId: string }[] },
  ctx: z.RefinementCtx,
) {
  const items = input.items ?? [];
  if (new Set(items.map((i) => i.itemId)).size !== items.length)
    ctx.addIssue({ code: 'custom', path: ['items'], message: 'Choose each part once.' });
}
const sendShape = {
  vanId: id,
  items: z.array(sendItem).max(5000).optional(),
  acknowledgeNegative: z.boolean().default(false),
};
export const restockPreviewSendInput = z
  .object(sendShape)
  .strict()
  .superRefine(uniqueRestockItems);
export type RestockPreviewSendInput = z.infer<typeof restockPreviewSendInput>;
export const restockSendInput = z
  .object({
    ...sendShape,
    ...keys,
    expectedRevision: revision,
    items: z.array(sendItem).max(5000),
  })
  .strict()
  .superRefine(uniqueRestockItems);
export type RestockSendInput = z.infer<typeof restockSendInput>;
export const restockCheckInput = z
  .object({
    ...keys,
    restockId: id,
    items: z.array(checkItem).max(5000).default([]),
    officeReturnLocationId: id.optional(),
  })
  .strict()
  .superRefine(uniqueRestockItems);
export type RestockCheckInput = z.infer<typeof restockCheckInput>;
export const restockListInput = z
  .object({
    vanId: id.optional(),
    beforeNumber: z.number().int().safe().positive().optional(),
  })
  .strict()
  .default({});
export type RestockListInput = z.infer<typeof restockListInput>;
export const restockGetInput = z.object({ restockId: id }).strict();
export type RestockGetInput = z.infer<typeof restockGetInput>;
export const restockDismissOwedInput = z
  .object({
    ...keys,
    vanId: id,
    itemId: id,
    expectedRevision: z.number().int().safe().positive(),
  })
  .strict();
export type RestockDismissOwedInput = z.infer<typeof restockDismissOwedInput>;

/** Quantity-only wire allowlists strip future nested financial fields from persisted answers. */
const amount = restockHundredths;
// z.object strips unknown keys recursively, including old/future stored financial fields.
export const restockSentResult = z.object({
  restockId: z.string().uuid().nullable(),
  restockNumber: z.number().int().positive().nullable(),
  state: z.enum(['sent', 'noop']),
  vanId: z.string().uuid(),
  items: z
    .array(
      z.object({
        itemId: z.string().uuid(),
        sentHundredths: amount,
        owedHundredths: amount,
      }),
    )
    .max(5000),
});
export const restockDismissedResult = z.object({
  vanId: z.string().uuid(),
  itemId: z.string().uuid(),
  owedHundredths: z.literal(0),
  revision: z.number().int().positive(),
});
const checkedItem = z
  .object({
    itemId: z.string().uuid(),
    sentHundredths: amount,
    confirmedHundredths: amount,
    missingHundredths: amount,
    damagedHundredths: amount,
    owedHundredths: amount,
  })
  .refine(
    (r) =>
      Number.isSafeInteger(
        r.confirmedHundredths + r.missingHundredths + r.damagedHundredths,
      ) &&
      r.confirmedHundredths + r.missingHundredths + r.damagedHundredths ===
        r.sentHundredths,
  );
export const restockCheckedResult = z.object({
  restockId: z.string().uuid(),
  restockNumber: z.number().int().positive(),
  vanId: z.string().uuid(),
  state: z.literal('checked'),
  checkedBy: z.string().uuid(),
  checkedByName: z.string().nullable(),
  checkedAt: z.string().datetime(),
  items: z.array(checkedItem).max(5000),
});
const summaryResult = z.object({
  restockId: z.string().uuid(),
  restockNumber: z.number().int().positive(),
  vanId: z.string().uuid(),
  vanName: z.string(),
  state: z.enum(['sent', 'checked']),
  sentAt: z.string().datetime(),
  sentBy: z.string().uuid(),
  sentByName: z.string().nullable(),
  checkedAt: z.string().datetime().nullable(),
  checkedBy: z.string().uuid().nullable(),
  itemCount: z.number().int().nonnegative(),
});
export const restockListResult = z.object({
  vanId: z.string().uuid().nullable(),
  items: z.array(summaryResult).max(50),
  uncheckedCount: z.number().int().safe().nonnegative(),
  itemTags: z.array(z.object({ itemId: z.string().uuid(), sentHundredths: amount })),
  hasMore: z.boolean(),
  nextBeforeNumber: z.number().int().positive().nullable(),
});
export const restockDetailResult = z.object({
  restockId: z.string().uuid(),
  restockNumber: z.number().int().positive(),
  vanId: z.string().uuid(),
  vanName: z.string(),
  state: z.enum(['sent', 'checked']),
  sentAt: z.string().datetime(),
  sentBy: z.string().uuid(),
  sentByName: z.string().nullable(),
  checkedAt: z.string().datetime().nullable(),
  checkedBy: z.string().uuid().nullable(),
  checkedByName: z.string().nullable(),
  items: z
    .array(
      z.object({
        itemId: z.string().uuid(),
        name: z.string(),
        sku: z.string().nullable(),
        unitLabel: z.string(),
        sentHundredths: amount,
        confirmedHundredths: amount.nullable(),
        missingHundredths: amount.nullable(),
        damagedHundredths: amount.nullable(),
        owedHundredths: amount,
      }),
    )
    .min(1)
    .max(5000),
  result: restockCheckedResult.nullable(),
  returnLocations: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
});

const batchVan = z.object({ vanId: id, items: z.array(sendItem).max(5000) }).strict();
const buySelection = z
  .object({
    feederId: id,
    itemId: id,
    quantityHundredths: restockHundredths.optional(),
  })
  .strict();
const reorderShape = {
  feederIds: z.array(id).max(200).optional(),
  sends: z.array(batchVan).max(200).optional(),
  buyItems: z.array(buySelection).max(200).optional(),
  acknowledgeNegative: z.boolean().default(false),
};
function uniqueReorder(
  input: {
    feederIds?: string[];
    sends?: { vanId: string; items: { itemId: string }[] }[];
    buyItems?: { feederId: string; itemId: string }[];
  },
  ctx: z.RefinementCtx,
) {
  const once = (values: string[], path: string) => {
    if (new Set(values).size !== values.length)
      ctx.addIssue({ code: 'custom', path: [path], message: 'Choose each entry once.' });
  };
  once(input.feederIds ?? [], 'feederIds');
  once(
    (input.sends ?? []).map((v) => v.vanId),
    'sends',
  );
  once(
    (input.buyItems ?? []).map((r) => `${r.feederId}|${r.itemId}`),
    'buyItems',
  );
  for (const van of input.sends ?? [])
    once(
      van.items.map((i) => i.itemId),
      'sends',
    );
  if ((input.sends ?? []).reduce((n, v) => n + v.items.length, 0) > 50000)
    ctx.addIssue({
      code: 'custom',
      path: ['sends'],
      message: 'Restock exceeds the 50,000-pair capacity.',
    });
}
export const reorderPlanInput = z
  .object(reorderShape)
  .strict()
  .superRefine(uniqueReorder);
export type ReorderPlanInput = z.infer<typeof reorderPlanInput>;
export const restockBatchSendInput = z
  .object({
    ...reorderShape,
    ...keys,
    feederIds: z.array(id).min(1).max(200),
    sends: z.array(batchVan).max(200),
    expectedPlanRevision: revision,
  })
  .strict()
  .superRefine(uniqueReorder);
export type RestockBatchSendInput = z.infer<typeof restockBatchSendInput>;
export const reorderDraftsInput = z
  .object({
    ...reorderShape,
    ...keys,
    feederIds: z.array(id).min(1).max(200),
    sends: z.array(batchVan).max(200),
    buyItems: z
      .array(
        buySelection
          .extend({ quantityHundredths: restockHundredths.positive().max(100_000_000) })
          .strict(),
      )
      .min(1)
      .max(200),
    expectedPlanRevision: revision,
  })
  .strict()
  .superRefine(uniqueReorder);
export type ReorderDraftsInput = z.infer<typeof reorderDraftsInput>;
export const restockBatchSentResult = z.object({
  restocks: z.array(restockSentResult).max(200),
});
export const reorderDraftsResult = z.object({
  created: z
    .array(
      z.object({
        id,
        poNumber: z.number().int().positive(),
        lineCount: z.number().int().positive(),
      }),
    )
    .max(200),
});

// Usage reviews identify existing configuration owners; no browser quantity is an input.
export const suggestionScope = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('template'), id }).strict(),
  z.object({ kind: z.literal('location'), id }).strict(),
]);
export type SuggestionScope = z.infer<typeof suggestionScope>;
const windowDays = z.union([z.literal(60), z.literal(90), z.literal(180)]);
const suggestionOffset = z.number().int().min(0).max(50000).default(0);
const selectedItems = z
  .array(id)
  .min(1)
  .max(5000)
  .refine((v) => new Set(v).size === v.length, 'Choose each part once.');
export const reorderWindowGetInput = z.void();
export const reorderWindowSetInput = z
  .object({ ...keys, windowDays, expectedRevision: revision })
  .strict();
export type ReorderWindowSetInput = z.infer<typeof reorderWindowSetInput>;
export const suggestionScopesInput = z
  .object({ offset: suggestionOffset, expectedRevision: revision.optional() })
  .strict()
  .refine(
    (v) => v.offset === 0 || !!v.expectedRevision,
    'A continuation requires its review revision.',
  )
  .default({ offset: 0 });
export const suggestionsInput = z
  .object({
    scope: suggestionScope,
    offset: suggestionOffset,
    expectedRevision: revision.optional(),
  })
  .strict()
  .refine(
    (v) => v.offset === 0 || !!v.expectedRevision,
    'A continuation requires its review revision.',
  );
export const suggestionHintsInput = z
  .object({ pairs: z.array(z.object({ locationId: id, itemId: id }).strict()).max(5000) })
  .strict()
  .refine(
    (v) =>
      new Set(v.pairs.map((p) => `${p.locationId}|${p.itemId}`)).size === v.pairs.length,
    'Choose each point once.',
  );
export const suggestionPreviewInput = z
  .object({
    scope: suggestionScope,
    selection: z.discriminatedUnion('kind', [
      z.object({ kind: z.literal('all') }).strict(),
      z.object({ kind: z.literal('items'), itemIds: selectedItems }).strict(),
    ]),
    expectedRevision: revision,
    offset: suggestionOffset,
    vanOffset: suggestionOffset,
  })
  .strict();
export const suggestionApplyInput = z
  .object({
    ...keys,
    scope: suggestionScope,
    itemIds: selectedItems,
    expectedRevision: revision,
  })
  .strict();
export type SuggestionScopesInput = z.infer<typeof suggestionScopesInput>;
export type SuggestionsInput = z.infer<typeof suggestionsInput>;
export type SuggestionHintsInput = z.infer<typeof suggestionHintsInput>;
export type SuggestionPreviewInput = z.infer<typeof suggestionPreviewInput>;
export type SuggestionApplyInput = z.infer<typeof suggestionApplyInput>;
export const suggestionWindowResult = z.object({
  storedWindowDays: windowDays.nullable(),
  windowDays,
  timezone: z.string(),
  revision,
});
export type SuggestionWindow = z.infer<typeof suggestionWindowResult>;
const datedWindow = suggestionWindowResult.extend({
  startDate: z.string(),
  endDate: z.string(),
  asOf: z.string().datetime(),
});
export const suggestionEvidenceResult = z.object({
  netHundredths: z.number().int().safe(),
  positiveObservations: z.number().int().nonnegative(),
  firstHistoryDate: z.string().nullable(),
  historyDays: z.number().int().nonnegative().nullable(),
  coverageDays: z.number().int().nonnegative(),
  eligible: z.boolean(),
  sourceLabel: z.string(),
  explanation: z.string(),
});
export type SuggestionEvidence = z.infer<typeof suggestionEvidenceResult>;
const followerResult = z.object({
  vanId: id,
  vanName: z.string(),
  eligible: z.boolean(),
  excludedReason: z.string().nullable(),
  evidence: suggestionEvidenceResult,
});
export type SuggestionFollower = z.infer<typeof followerResult>;
const suggestionStatus = z.enum([
  'actionable',
  'matches_use',
  'not_enough_usage',
  'blocked',
]);
export const suggestionRowResult = z.object({
  itemId: id,
  itemName: z.string(),
  unitLabel: z.string(),
  source: z.enum(['template', 'local', 'van_only', 'by_hand']),
  currentKeepHundredths: amount,
  currentFillHundredths: amount,
  suggestedKeepHundredths: amount.nullable(),
  suggestedFillHundredths: amount.nullable(),
  status: suggestionStatus,
  explanation: z.string(),
  evidence: suggestionEvidenceResult,
  followers: z.array(followerResult).max(100),
  affectedVanCount: z.number().int().nonnegative(),
  localExclusionCount: z.number().int().nonnegative(),
});
export type SuggestionRow = z.infer<typeof suggestionRowResult>;
export const suggestionScopeListResult = z.object({
  revision,
  rows: z
    .array(
      z.object({
        scope: suggestionScope,
        name: z.string(),
        pairCount: z.number().int().nonnegative(),
        available: z.boolean(),
        explanation: z.string().nullable(),
      }),
    )
    .max(25),
  total: z.number().int().nonnegative(),
  nextOffset: z.number().int().nullable(),
});
export type SuggestionScopeList = z.infer<typeof suggestionScopeListResult>;
const suggestionScopeResult = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('template'), id }),
  z.object({ kind: z.literal('location'), id }),
]);
export const suggestionsResult = z.object({
  inheritedScopes: z
    .array(
      z.object({
        scope: suggestionScopeResult,
        name: z.string(),
        pairCount: z.number().int().nonnegative(),
      }),
    )
    .max(1),
  scope: suggestionScope,
  scopeName: z.string(),
  revision,
  window: datedWindow,
  rows: z.array(suggestionRowResult).max(25),
  counts: z.object({
    actionable: z.number().int().nonnegative(),
    matches_use: z.number().int().nonnegative(),
    not_enough_usage: z.number().int().nonnegative(),
    blocked: z.number().int().nonnegative(),
  }),
  total: z.number().int().nonnegative(),
  nextOffset: z.number().int().nullable(),
});
export type SuggestionsResult = z.infer<typeof suggestionsResult>;
export const suggestionHintsResult = z.object({
  hints: z
    .array(
      z.object({
        locationId: id,
        itemId: id,
        scope: suggestionScope.nullable(),
        status: suggestionStatus,
        suggestedKeepHundredths: amount.nullable(),
        suggestedFillHundredths: amount.nullable(),
        explanation: z.string(),
      }),
    )
    .max(5000),
});
export type SuggestionHintsResult = z.infer<typeof suggestionHintsResult>;
export const suggestionPreviewResult = z.object({
  scope: suggestionScope,
  scopeName: z.string(),
  scopeRevision: revision,
  revision,
  itemIds: z.array(id).max(5000),
  rows: z.array(suggestionRowResult).max(25),
  affectedVans: z.array(z.object({ vanId: id, vanName: z.string() })).max(25),
  affectedVanCount: z.number().int().nonnegative(),
  localExclusionCount: z.number().int().nonnegative(),
  nextOffset: z.number().int().nullable(),
  nextVanOffset: z.number().int().nullable(),
  window: datedWindow,
});
export type SuggestionPreviewResult = z.infer<typeof suggestionPreviewResult>;
export const suggestionApplyResult = z.object({
  scope: suggestionScopeResult,
  changedItemIds: z.array(id).max(5000),
  appliedAt: z.string().datetime(),
  windowDays,
  revision,
});
export type SuggestionApplyResult = z.infer<typeof suggestionApplyResult>;
